import { formatDateDisplay } from '../../utils/helperFunctions';
import { toCalendarDateString } from '../../utils/dateInputUtils';
import { ITEM_STATUS_CODES } from '../../utils/constants';
import type { tDiscrepancyItem } from '../../types';
import type { SingleUnit } from '../../types/schema';

/** e.g. "Today, 2:51 PM" or "03/15/2026, 2:51 PM" */
export function formatInspectionTimestamp(raw: string | null | undefined): string | null {
    if (!raw) {
        return null;
    }

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const now = new Date();
    const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    const calendar = toCalendarDateString(raw);

    if (date.toDateString() === now.toDateString()) {
        return `Today, ${time}`;
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
        return `Yesterday, ${time}`;
    }

    const formattedDate = calendar ? formatDateDisplay(calendar) : formatDateDisplay(raw);
    return formattedDate !== '-' ? `${formattedDate}, ${time}` : time;
}

export function formatUmReading(value: number | null | undefined, decimals = 0): string | null {
    if (value == null || !Number.isFinite(value)) {
        return null;
    }

    return value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

export function resolveUmSinceLastService(unit: SingleUnit | null | undefined): number | null {
    const current = unit?.currentUMCounterValue;
    const lastService = unit?.lastServiceHours;

    if (
        current == null ||
        lastService == null ||
        !Number.isFinite(current) ||
        !Number.isFinite(lastService)
    ) {
        return null;
    }

    return current - lastService;
}

export function countOpenIssues(
    discrepancies: tDiscrepancyItem[] | null | undefined,
    equipmentId: string | undefined,
    unitId: string | null | undefined,
): number {
    if (!discrepancies?.length || !equipmentId) {
        return 0;
    }

    return discrepancies.filter((item) => {
        if (item.equipmentId !== equipmentId || item.status === ITEM_STATUS_CODES.ok) {
            return false;
        }

        if (unitId) {
            return item.unitId === unitId;
        }

        return true;
    }).length;
}

export type InspectionOperationalStatus = 'active' | 'under_review' | 'out_of_service';

export function resolveOperationalStatus(
    primaryDiscrepancyStatus: string | null,
): InspectionOperationalStatus {
    if (primaryDiscrepancyStatus === ITEM_STATUS_CODES.inop) {
        return 'out_of_service';
    }

    if (primaryDiscrepancyStatus === ITEM_STATUS_CODES.under_review) {
        return 'under_review';
    }

    return 'active';
}

export const INSPECTION_OPERATIONAL_STATUS_LABEL: Record<InspectionOperationalStatus, string> = {
    active: 'Active',
    under_review: 'Under Review',
    out_of_service: 'Out of Service',
};
